import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { select } from 'd3-selection';
import { scaleLinear, scaleSqrt } from 'd3-scale';
import { partition, hierarchy } from 'd3-hierarchy';
import { arc } from 'd3-shape';
import { ascending } from 'd3-array';
import { interpolate } from 'd3-interpolate';
import { fetchJson } from '../../../../helpers/fetch.js';
import Tooltip from '../../../common/Tooltip.jsx';
import LoadingIndicator from '../../../common/loading_indicator.jsx';
import { useWidgetInnerSize } from '../../hooks/useWidgetInnerSize.js';

import {
    monitoring_dashboard_sdg_widget_path
} from '~/routes';

const COLORS = {
    1: '#ec1629',
    2: '#d4a224',
    3: '#229d47',
    4: '#c4192f',
    5: '#ef3d27',
    6: '#02afda',
    7: '#fdb80b',
    8: '#901035',
    9: '#f46e20',
    10: '#e20d85',
    11: '#f99f21',
    12: '#d08e25',
    13: '#47773b',
    14: '#027ebd',
    15: '#3bb148',
    16: '#01558c',
    17: '#113368'
}

const WidgetExplanation = ({ widget, clicked: clickedElems, width, info }) => {
  const clicked = clickedElems.length > 0;

  return (
    <div className="explanation" style={{ width, height: width, color: clicked ? 'white' : '#676767', }}>
        <span className="project-count" style={{fontSize: width * 0.2}}>{info.projectCount}</span>
        <br />
        <span className="projects-targeting" style={{fontSize: width * 0.1, color: clicked ? '#b9b9b9' : '#767676'}}>
            {I18n.t("projects_targeting")}
        </span>
        <br />
        <span className="sdg-name" style={{
            marginTop: clicked ? (widget.radius - 20) * 0.3 : 0,
            fontSize: clicked ? (widget.radius - 20) * 0.08 : (widget.radius - 20) * 0.1,
        }}>{info.explanationSdgName}</span>
    </div>
  );
};

const SDGTotalContributionWidget = ({ widgetId, widgetRef, theme }) => {
    const svgRef = useRef();
    const [info, setInfo] = useState({sdgId: '', explanationSdgName: '', infoSdgName: '', projectCount: ''});
    const [visibility, setVisibility] = useState(({centerLogo: true, explanation: false, infoContainer: false}));
    const [animation, setAnimation] = useState(false);
    const [tooltipActive, setTooltipActive] = useState(false);
    const [tooltipTitle, setTooltipTitle] = useState(null);
    const [widgetData, setWidgetData] = useState(null);
    const [clicked, setClicked] = useState([]);
    const clickedRef = useRef(clicked)
    const size = useWidgetInnerSize(widgetRef, theme);
    const [nodeByKey, setNodeByKey] = useState({});
    const demo_mode = useSelector(state => state.dashboard.widgets[widgetId].settings?.demo_mode);
    const projectId = useSelector(state => state.dashboard.projectId);

    const { data, isLoading, error } = useQuery({
        queryKey: ['sdgTotalContribution', projectId, demo_mode],
        queryFn: async () => fetchJson(monitoring_dashboard_sdg_widget_path({ project_id: projectId, random: String(demo_mode) })),
        enabled: !!projectId,
    });

    useEffect(() => {
        clickedRef.current = clicked;
    }, [clicked]);

    const updateInfo = (updates) => setInfo(prev => ({ ...prev, ...updates }));

    const updateVisibility = (updates) => setVisibility(prev => ({ ...prev, ...updates }));

    const init = (data) => {
        const { width, height } = size;

        var radius = (Math.min(width, height) / 2);

        var x = scaleLinear().range([0, 2 * Math.PI]);
        var y = scaleSqrt().range([0, radius]);

        // Arc angles
        var arcAngles = arc()
            .startAngle(function (d) { return Math.max(0, Math.min(2 * Math.PI, x(d.x0))); })
            .endAngle(function (d) { return Math.max(0, Math.min(2 * Math.PI, x(d.x1))); })
            .innerRadius(function (d) { return Math.max(0, y(d.y0)); })
            .outerRadius(function (d) { return Math.max(0, y(d.y1)); });

        // Add the svg container
        const svg = select(widgetRef.current)
            .select("svg")
            .select(".chart-container");

        const widget = {
            data,
            clicked: [],
            radius,
            x,
            y,
            partition: partition(),
            arc: arcAngles,
            svg: svg // same as before, just selected instead of appended
        };

        setWidgetData(widget);
    }

    const reset = () => {
        select(svgRef.current).select(".chart-container").selectAll("*").remove();
        setClicked([]);
        setVisibility({centerLogo: true, explanation: false, infoContainer: false});
    }

    const createVisualization = (json) => {
        reset();
        // Bounding circle underneath the chart, to make it easier to detect
        // when the mouse leaves the area.
        widgetData.svg.append("svg:circle")
            .attr("r", widgetData.radius)
            .style("opacity", 0);

        // Helpers in sizing the arcs
        var sizeFactor = 1.0;
        var factors = {};

        const nodeByKey = {};
        // Turn the data into a d3 hierarchy and calculate the sums.
        var root = hierarchy(json, function children(d) { return d.targets; })
            // Used to calculate size factors for arcs.
            .each(function (d) {
                if (d.depth === 1 && d.data.id) {
                    d.lookupKey = d.data.id;
                } else if (d.depth === 2 && d.data.str_id) {
                    d.lookupKey = d.data.str_id;
                }

                if (d.lookupKey) {
                    nodeByKey[d.lookupKey] = d;
                }

                if (d.data.id) {
                    var value = d.data.count;
                    if (d.data.targets) {
                        d.data.targets.forEach(function (t) { value -= t.count });
                        var factor = (value * -1 + d.data.count) / d.data.count;
                        factors[d.data.id] = factor;
                        if (factor > sizeFactor) sizeFactor = factor;
                    }
                }
            })
            // Provide the relative value of the arcs
            .sum(function (d) {
                var value = 0;
                if (d.id) {
                    if (d.targets) {
                        var targets = 0;
                        d.targets.forEach(function (t) { targets += (sizeFactor / factors[t.sdg_id] * t.count) });
                        value = sizeFactor * d.count - (isNaN(targets) ? 0 : targets);
                    } else {
                        value = sizeFactor / factors[d.sdg_id] * d.count;
                    }
                }
                return value;
            })
            .sort(function (a, b) { return ascending(a.data.id, b.data.id); });

        var nodes = widgetData.partition(root).descendants()

        // Add nodes to svg
        widgetData.svg.data([json]).selectAll("path")
            .data(nodes)
            .enter().append("svg:path")
            .attr("d", widgetData.arc)
            .style("fill", function (d) { return d.depth ? (d.data.sdg_id ? COLORS[d.data.sdg_id] : COLORS[d.data.id]) : 'white'; }.bind(this))
            .style("opacity", function (d) { return d.data.targets ? 1 : 0.5; })
            .attr("data-node-id", d => d.lookupKey || "");

        setNodeByKey(nodeByKey);
    }

    useEffect(() => {
        const svg = select(svgRef.current);
        svg.selectAll('path')
        .on('mouseover', function (event, d) {
            mouseover(d);
        });

        return () => {
            svg.selectAll('path').on('mouseover', null);
        };
    }, [nodeByKey]);

    const handleClick = e => {
        const key = e.target.dataset.nodeId;
        click(nodeByKey[key]);
    };

    const click = (d, b) => {
        if (d?.data.sdg_id) {
            if (clicked.indexOf(String(d.data.str_id)) != -1) return;
            setClicked([String(d.data.str_id), String(d.data.sdg_id)]);
        } else if (d?.data.id) {
            if (clicked.length == 1 && clicked.indexOf(String(d.data.id)) != -1) return;
            setClicked([String(d.data.id)]);
        } else {
            if (clicked.length == 0) return;
            setClicked([]);
        }

        if (animation) return;
        setAnimation(true);
        mouseleave(d);

        updateVisibility({explanation: false, infoContainer: false, centerLogo: false});

        widgetData.svg.transition()
            .duration(750)
            .tween("scale", function () {
                var xd = interpolate(widgetData.x.domain(), [d?.x0 || 0, d?.x1 || 1]),
                    yd = interpolate(widgetData.y.domain(), [d?.y0 || 0, 1]),
                    yr = interpolate(widgetData.y.range(), [d?.y0 ? 20 : 0, widgetData.radius]);
                return function (t) { widgetData.x.domain(xd(t)); widgetData.y.domain(yd(t)).range(yr(t)); };
            }.bind(this))
            .selectAll("path")
            .attrTween("d", function (d) { return function () { return widgetData.arc(d); }; });

        setTimeout(() => showSdgInfo(d), 750);
    }

    const mouseleave = (d) => {
        updateVisibility({explanation: false, infoContainer: false, centerLogo: false});
        if (!widgetData.animation) {
            if (clickedRef.current.length === 1) {
                updateVisibility({infoContainer: true});
            } else if (clickedRef.current.length === 2) {
                updateVisibility({infoContainer: true});
            } else {
                updateVisibility({centerLogo: true});
            }
        }
        // Transition each segment to full opacity and then reactivate it.
        widgetData.svg.selectAll("path").style("opacity", function (d) { return d.data.targets ? 1 : 0.5; })
    }

    const showSdgInfo = (d, widget) => {
        if (d?.data.id) {
            setTooltipTitle(d.data.definition);
            setTooltipActive(true);

            if (d.data.targets) {
                var id = 'SDG ' + String(d.data.id);
                var name = d.data.short_name.toUpperCase();
                updateInfo({sdgId: id, infoSdgName: name});
                updateVisibility({explanation: false, infoContainer: true});

                select(widgetRef.current).selectAll("path").style("stroke", function (d) { return d.data.targets ? "none" : "#fff"; })
            } else {
                var id = d.data.str_id;
                var name = d.data.short_name;
                updateInfo({sdgId: id, infoSdgName: name});
                updateVisibility({explanation: false, infoContainer: true});
                select(widgetRef.current).selectAll("path").style("stroke", "none");
            }
        } else {
            select(widgetRef.current).selectAll("path").style("stroke", "#fff");
            updateVisibility({centerLogo: true});
        }

        setAnimation(false);
    }

    const mouseover = (d) => {
        if (animation) return;

        if (!d.data.definition || !d.depth) {
            mouseleave(d);
            return;
        };

        const projectCount = d.data.count;

        const len = clickedRef.current.length;
        const inner = d.depth > 1;
        const showInfo = (len === 1 && !inner) || (!d.data.targets && len === 2);

        let sdgText;
        if (!showInfo && inner) {
            sdgText = `${d.data.str_id}: ${d.data.short_name}`;
        } else if (len === 1) {
            sdgText = d.data.short_name.toUpperCase();
        } else if (!d.data.targets && len === 2) {
            sdgText = d.data.short_name;
        } else {
            sdgText = `”SDG ${d.data.id}: ${d.data.short_name}”`;
        }

        updateVisibility({ explanation: !showInfo, infoContainer: showInfo, centerLogo: false });
        updateInfo({ projectCount, [showInfo ? 'infoSdgName' : 'explanationSdgName']: sdgText });

        var sequenceArray = d.ancestors().reverse();
        sequenceArray.shift(); // remove root node from the array

        // Fade all the segments.
        widgetData.svg.selectAll("path").style("opacity", 0.3);

        // Then highlight only those that are an ancestor of the current segment.
        widgetData.svg.selectAll("path")
            .filter(function (node) {
                return (sequenceArray.indexOf(node) >= 0) && !(len === 2 && node.depth === 2)
            })
            .style("opacity", 1);
    }

    useEffect(() => {
        if (data && svgRef.current) {
            init(data);
        }
    }, [isLoading, size, svgRef.current]);

    useEffect(() => {
        if (widgetData && data && svgRef.current) {
            setTimeout(() => {
                createVisualization(data);
            }, 0)
        }
    }, [widgetData, svgRef.current]);

    const width = useMemo(() => {
        return (widgetData?.radius - 20 || 0);
    }, [widgetData]);

    if (isLoading) {
        return (<LoadingIndicator />);
    }

    return (
        <div className="sdg-widget-container" style={{ width: '100%', height: '100%' }}>
            <div className="sdg-total-contribution-chart">
                {clicked.length === 0 && !animation && visibility.centerLogo && <div className="sdg-center-logo" style={{
                    width: width * 0.85, height: width * 0.85
                }}></div>}

                {widgetData && !animation && visibility.explanation && <WidgetExplanation width={width} widget={widgetData} clicked={clicked} info={info} />}

                {!animation && visibility.infoContainer && <div className="sdg-info-container" style={{ width, height: width }}>
                    <span className="sdg-id" style={{ fontSize: width * 0.2 }}>{info.sdgId}</span>
                    <br />
                    <h3 className="sdg-name" style={{ fontSize: width * 0.1, marginTop: width * 0.475 }}>{info.infoSdgName}</h3>
                    <br />
                    {<Tooltip title={tooltipTitle} trigger='click' active={tooltipActive} className='sdg-info-tooltip'>
                        <span className="sdg-definition tag tag-info help" style={{ marginTop: '0.5rem', fontSize: ((widgetData?.radius || 35) - 20) * 0.08, width: ((widgetData?.radius || 35) - 20) * 0.10 }}>
                            ?
                        </span>
                    </Tooltip>}
                </div>}
                <svg ref={svgRef} width={size.width} height={size.height} onClick={handleClick}>
                    <g
                        className="chart-container"
                        transform={`translate(${size.width / 2}, ${size.height / 2})`}
                        onMouseLeave={() => mouseleave()}
                    />
                </svg>

            </div>
        </div>

    );
};

export default SDGTotalContributionWidget;